/**
 * Executive Insights - the strategic rollups that were previously captured
 * as raw data but never aggregated: staff turnover, alumni outcomes, PD
 * ROI, survey/NPS-style insight, multi-year cohort trends, predictive
 * capacity planning, payroll-vs-budget, asset utilization, external
 * exam-board benchmarking, and the compliance/accreditation register.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import executiveReportsService from '../services/executiveReportsService';
import classesService from '../services/classesService';
import api from '../utils/api';
import { CircleNotch } from '@phosphor-icons/react';
import './ExecutiveInsightsPage.css';

const TABS = [
  { key: 'turnover', label: 'Staff Turnover' },
  { key: 'workforce', label: 'Workforce Analytics' },
  { key: 'alumni', label: 'Alumni Outcomes' },
  { key: 'pd-roi', label: 'PD ROI' },
  { key: 'survey', label: 'Survey Insights' },
  { key: 'cohorts', label: 'Cohort Trends' },
  { key: 'capacity', label: 'Capacity Forecast' },
  { key: 'payroll-budget', label: 'Payroll vs Budget' },
  { key: 'assets', label: 'Asset Utilization' },
  { key: 'exam-benchmark', label: 'Exam Benchmarking' },
  { key: 'compliance', label: 'Compliance Register' },
];

function ExecutiveInsightsPage() {
  // Deep-linkable via ?tab=<key> — lets other pages (e.g. the Executive
  // Dashboard's widget tiles) drill straight into the relevant detail tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(TABS.some((t) => t.key === requestedTab) ? requestedTab : 'turnover');

  useEffect(() => {
    if (requestedTab && TABS.some((t) => t.key === requestedTab) && requestedTab !== tab) {
      setTab(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  const changeTab = (key) => {
    setTab(key);
    setSearchParams({ tab: key });
  };

  return (
    <Layout>
      <div className="page executive-insights-page">
        <div className="page-header">
          <div>
            <h1>Executive Insights</h1>
            <p>Turnover, alumni outcomes, training ROI, survey trends, cohort tracking, capacity forecasting, payroll-vs-budget, asset utilization, exam benchmarking, and compliance</p>
          </div>
        </div>

        <div className="eip-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => changeTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'turnover' && <TurnoverTab />}
        {tab === 'workforce' && <WorkforceTab />}
        {tab === 'alumni' && <AlumniTab />}
        {tab === 'pd-roi' && <PdRoiTab />}
        {tab === 'survey' && <SurveyTab />}
        {tab === 'cohorts' && <CohortsTab />}
        {tab === 'capacity' && <CapacityTab />}
        {tab === 'payroll-budget' && <PayrollBudgetTab />}
        {tab === 'assets' && <AssetsTab />}
        {tab === 'exam-benchmark' && <ExamBenchmarkTab />}
        {tab === 'compliance' && <ComplianceTab />}
      </div>
    </Layout>
  );
}

function KeyValueTable({ data, keyLabel = 'Key', valueLabel = 'Count' }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <p className="empty">No data</p>;
  return (
    <div className="eip-table">
      <table>
        <thead><tr><th>{keyLabel}</th><th>{valueLabel}</th></tr></thead>
        <tbody>{entries.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- */

function TurnoverTab() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await executiveReportsService.getStaffTurnover({ months })); }
    catch (error) { toast.error('Failed to load staff turnover: ' + error.message); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter"><label>Window (months)</label><input type="number" min="1" max="36" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} /></div>
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.attrition_rate}%</span><span className="eip-stat-label">Attrition Rate</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.active_staff}</span><span className="eip-stat-label">Active Staff</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.exits_in_window}</span><span className="eip-stat-label">Exits ({months}mo)</span></div>
        </div>
        <h4>Tenure Distribution</h4>
        <KeyValueTable data={data.tenure_distribution} keyLabel="Tenure" />
        <h4>Exits by Reason</h4>
        <KeyValueTable data={data.exits_by_reason} keyLabel="Reason" />
        <h4>Exits by Type</h4>
        <KeyValueTable data={data.exits_by_type} keyLabel="Type" />
      </div>
    </div>
  );
}

function WorkforceTab() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await executiveReportsService.getWorkforceAnalytics({ months })); }
    catch (error) { toast.error('Failed to load workforce analytics: ' + error.message); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter"><label>Window (months)</label><input type="number" min="1" max="36" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} /></div>
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.active_staff}</span><span className="eip-stat-label">Active Staff</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.hires_in_window}</span><span className="eip-stat-label">Hires ({months}mo)</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.exits_in_window}</span><span className="eip-stat-label">Exits ({months}mo)</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.net_change_in_window > 0 ? '+' : ''}{data.net_change_in_window}</span><span className="eip-stat-label">Net Change</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.average_time_to_fill_days ?? '—'}</span><span className="eip-stat-label">Avg. Time to Fill (days)</span></div>
        </div>

        <h4>Staff by Department</h4>
        <KeyValueTable data={data.by_department} keyLabel="Department" />

        <h4>Hires vs Exits by Month</h4>
        {(data.net_change_by_month || []).length === 0 ? <p className="empty">No data</p> : (
          <div className="eip-table">
            <table>
              <thead><tr><th>Month</th><th>Hires</th><th>Exits</th><th>Net Change</th></tr></thead>
              <tbody>
                {data.net_change_by_month.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td>{row.hires}</td>
                    <td>{row.exits}</td>
                    <td>{row.net_change > 0 ? '+' : ''}{row.net_change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AlumniTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    executiveReportsService.getAlumniOutcomes().then(setData).catch((e) => toast.error('Failed to load alumni outcomes: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="eip-card">
        <div className="eip-stat-row"><div className="eip-stat"><span className="eip-stat-value">{data.total_alumni}</span><span className="eip-stat-label">Total Alumni</span></div></div>
        <h4>By Graduation Year</h4>
        {data.by_graduation_year.length === 0 ? <p className="empty">No data</p> : (
          <div className="eip-table"><table>
            <thead><tr><th>Year</th><th>Total</th><th>Institution Placement</th><th>Employment Rate</th></tr></thead>
            <tbody>{data.by_graduation_year.map((r) => (
              <tr key={r.graduation_year}><td>{r.graduation_year}</td><td>{r.total}</td><td>{r.institution_placement_rate}%</td><td>{r.employment_rate}%</td></tr>
            ))}</tbody>
          </table></div>
        )}
        <h4>Top Occupations</h4>
        {data.top_occupations.length === 0 ? <p className="empty">No data</p> : (
          <div className="eip-table"><table>
            <thead><tr><th>Occupation</th><th>Count</th></tr></thead>
            <tbody>{data.top_occupations.map((o) => <tr key={o.occupation}><td>{o.occupation}</td><td>{o.count}</td></tr>)}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function PdRoiTab() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await executiveReportsService.getPdRoi({ months })); }
    catch (error) { toast.error('Failed to load PD ROI: ' + error.message); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter"><label>Window (months)</label><input type="number" min="1" max="36" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} /></div>
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.trainings_completed}</span><span className="eip-stat-label">Trainings Completed</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.total_training_hours}</span><span className="eip-stat-label">Total Hours</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.staff_trained}</span><span className="eip-stat-label">Staff Trained</span></div>
        </div>
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.avg_performance_rating_trained ?? '—'}</span><span className="eip-stat-label">Avg Rating (Trained)</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.avg_performance_rating_untrained ?? '—'}</span><span className="eip-stat-label">Avg Rating (Untrained)</span></div>
        </div>
      </div>
    </div>
  );
}

function SurveyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    executiveReportsService.getSurveyInsights().then(setData).catch((e) => toast.error('Failed to load survey insights: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.overall_average_rating ?? '—'}</span><span className="eip-stat-label">Overall Avg Rating</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.rated_responses}</span><span className="eip-stat-label">Rated Responses</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.total_responses}</span><span className="eip-stat-label">Total Responses</span></div>
        </div>
        <h4>Average Rating by Month</h4>
        <KeyValueTable data={data.average_rating_by_month} keyLabel="Month" valueLabel="Avg Rating" />
        <h4>Average Rating by Survey Type</h4>
        <KeyValueTable data={data.average_rating_by_survey_type} keyLabel="Type" valueLabel="Avg Rating" />
      </div>
    </div>
  );
}

function CohortsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    executiveReportsService.getCohortTrends().then(setData).catch((e) => toast.error('Failed to load cohort trends: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      {data.cohorts.length === 0 ? <div className="empty-state"><p>No admission data yet</p></div> : (
        <div className="eip-table"><table>
          <thead><tr><th>Admission Year</th><th>Total</th><th>Active</th><th>Graduated</th><th>Transferred</th><th>Withdrawn</th><th>Retention Rate</th></tr></thead>
          <tbody>{data.cohorts.map((c) => (
            <tr key={c.admission_year}>
              <td>{c.admission_year}</td><td>{c.total}</td><td>{c.active}</td><td>{c.graduated}</td>
              <td>{c.transferred}</td><td>{c.withdrawn}</td><td>{c.retention_rate}%</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

function CapacityTab() {
  const [termsAhead, setTermsAhead] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await executiveReportsService.getCapacityForecast({ terms_ahead: termsAhead })); }
    catch (error) { toast.error('Failed to load capacity forecast: ' + error.message); }
    finally { setLoading(false); }
  }, [termsAhead]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter"><label>Terms Ahead</label><input type="number" min="1" max="8" value={termsAhead} onChange={(e) => setTermsAhead(parseInt(e.target.value, 10) || 2)} /></div>
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.current_active_students}</span><span className="eip-stat-label">Active Students</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.total_capacity}</span><span className="eip-stat-label">Total Capacity</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.current_utilization_pct ?? '—'}%</span><span className="eip-stat-label">Current Utilization</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.annual_growth_rate_pct}%</span><span className="eip-stat-label">Annual Growth Rate</span></div>
        </div>
        <h4>Projections</h4>
        <div className="eip-table"><table>
          <thead><tr><th>Term</th><th>Projected Students</th><th>Projected Utilization</th><th>Over Capacity?</th></tr></thead>
          <tbody>{data.projections.map((p) => (
            <tr key={p.term}><td>{p.term}</td><td>{p.projected_students}</td><td>{p.projected_utilization_pct ?? '—'}%</td><td>{p.over_capacity ? 'Yes' : 'No'}</td></tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}

function PayrollBudgetTab() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/api/fiscal-periods/').then((d) => setPeriods(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try { setData(await executiveReportsService.getPayrollVsBudget(periodId)); }
    catch (error) { toast.error('Failed to load payroll vs budget: ' + error.message); }
    finally { setLoading(false); }
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter">
        <label>Fiscal Period</label>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
          <option value="">Select…</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.period_name}</option>)}
        </select>
      </div>
      {periods.length === 0 && <p className="eip-hint">No fiscal periods set up yet — create one under Finance.</p>}
      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : data && (
        <div className="eip-card">
          <div className="eip-stat-row">
            <div className="eip-stat"><span className="eip-stat-value">GHS {data.total_budget.toLocaleString()}</span><span className="eip-stat-label">Total Budget</span></div>
            <div className="eip-stat"><span className="eip-stat-value">GHS {data.total_payroll_net.toLocaleString()}</span><span className="eip-stat-label">Total Payroll (Net)</span></div>
            <div className="eip-stat"><span className="eip-stat-value">{data.payroll_pct_of_budget ?? '—'}%</span><span className="eip-stat-label">Payroll % of Budget</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetsTab() {
  const [years, setYears] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await executiveReportsService.getAssetUtilization({ replacement_age_years: years })); }
    catch (error) { toast.error('Failed to load asset utilization: ' + error.message); }
    finally { setLoading(false); }
  }, [years]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="eip-panel">
      <div className="form-group eip-inline-filter"><label>Replacement Age (years)</label><input type="number" min="1" max="30" value={years} onChange={(e) => setYears(parseInt(e.target.value, 10) || 5)} /></div>
      <div className="eip-card">
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{data.total_assets}</span><span className="eip-stat-label">Total Assets</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.utilization_pct}%</span><span className="eip-stat-label">In-Use Rate</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{data.replacement_due_count}</span><span className="eip-stat-label">Replacement Due</span></div>
          <div className="eip-stat"><span className="eip-stat-value">GHS {data.estimated_replacement_cost.toLocaleString()}</span><span className="eip-stat-label">Est. Replacement Cost</span></div>
        </div>
        <h4>By Status</h4>
        <KeyValueTable data={data.by_status} keyLabel="Status" />
        <h4>By Condition</h4>
        <KeyValueTable data={data.by_condition} keyLabel="Condition" />
        {data.replacement_due_assets.length > 0 && (
          <>
            <h4>Assets Due for Replacement</h4>
            <div className="eip-table"><table>
              <thead><tr><th>Name</th><th>Tag</th><th>Purchased</th><th>Condition</th></tr></thead>
              <tbody>{data.replacement_due_assets.map((a) => <tr key={a.id}><td>{a.name}</td><td>{a.tag_number}</td><td>{a.purchase_date}</td><td>{a.condition}</td></tr>)}</tbody>
            </table></div>
          </>
        )}
      </div>
    </div>
  );
}

function ExamBenchmarkTab() {
  const [subjects, setSubjects] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ exam_name: 'BECE', exam_year: String(new Date().getFullYear()), subject_id: '', national_average_score: '', regional_average_score: '', source: '' });
  const [submitting, setSubmitting] = useState(false);
  const [compareName, setCompareName] = useState('BECE');
  const [compareYear, setCompareYear] = useState(String(new Date().getFullYear()));
  const [comparison, setComparison] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setBenchmarks(await executiveReportsService.listExamBenchmarks()); }
    catch (error) { toast.error('Failed to load benchmarks: ' + error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { classesService.listAllSubjects().then((d) => setSubjects(d || [])).catch(() => {}); }, []);

  const subjectName = (id) => subjects.find((s) => s.id === id)?.name || id;

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.subject_id) { toast.error('Select a subject'); return; }
    setSubmitting(true);
    try {
      await executiveReportsService.createExamBenchmark({
        ...form,
        national_average_score: form.national_average_score ? parseFloat(form.national_average_score) : null,
        regional_average_score: form.regional_average_score ? parseFloat(form.regional_average_score) : null,
      });
      toast.success('Benchmark added');
      setShowCreate(false);
      load();
    } catch (error) {
      toast.error('Failed to add benchmark: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try { await executiveReportsService.deleteExamBenchmark(id); toast.success('Deleted'); load(); }
    catch (error) { toast.error('Failed to delete: ' + error.message); }
  };

  const runComparison = async () => {
    try { setComparison(await executiveReportsService.getExamBenchmarkComparison(compareName, compareYear)); }
    catch (error) { toast.error('Failed to compare: ' + error.message); }
  };

  return (
    <div className="eip-panel eip-two-col">
      <div className="eip-card">
        <h3>Benchmark Reference Data</h3>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add Benchmark</button>
        {loading ? <div className="loading"><CircleNotch size={24} weight="bold" /> Loading...</div> : (
          <div className="eip-table eip-mt"><table>
            <thead><tr><th>Exam</th><th>Year</th><th>Subject</th><th>National Avg</th><th>Regional Avg</th><th></th></tr></thead>
            <tbody>{benchmarks.map((b) => (
              <tr key={b.id}>
                <td>{b.exam_name}</td><td>{b.exam_year}</td><td>{subjectName(b.subject_id)}</td>
                <td>{b.national_average_score ?? '—'}</td><td>{b.regional_average_score ?? '—'}</td>
                <td><button className="btn btn-sm btn-danger" onClick={() => remove(b.id)}>Delete</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}

        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header"><h2>Add Benchmark</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
              <form onSubmit={submitCreate}>
                <div className="form-group"><label>Exam Name</label><input value={form.exam_name} onChange={(e) => setForm({ ...form, exam_name: e.target.value })} required /></div>
                <div className="form-group"><label>Exam Year</label><input value={form.exam_year} onChange={(e) => setForm({ ...form, exam_year: e.target.value })} required /></div>
                <div className="form-group">
                  <label>Subject</label>
                  <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} required>
                    <option value="">Select…</option>
                    {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>National Average Score</label><input type="number" step="0.1" value={form.national_average_score} onChange={(e) => setForm({ ...form, national_average_score: e.target.value })} /></div>
                <div className="form-group"><label>Regional Average Score</label><input type="number" step="0.1" value={form.regional_average_score} onChange={(e) => setForm({ ...form, regional_average_score: e.target.value })} /></div>
                <div className="form-group"><label>Source</label><input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. WAEC Chief Examiner's Report" /></div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <div className="eip-card">
        <h3>Compare This School vs Benchmark</h3>
        <div className="eip-picker-row">
          <div className="form-group"><label>Exam Name</label><input value={compareName} onChange={(e) => setCompareName(e.target.value)} /></div>
          <div className="form-group"><label>Exam Year</label><input value={compareYear} onChange={(e) => setCompareYear(e.target.value)} /></div>
          <button className="btn btn-secondary" onClick={runComparison}>Compare</button>
        </div>
        {comparison && (
          comparison.subjects.length === 0 ? <p className="empty">No results recorded for this exam/year yet</p> : (
            <div className="eip-table"><table>
              <thead><tr><th>Subject</th><th>School Avg</th><th>National Avg</th><th>Regional Avg</th><th>vs National</th></tr></thead>
              <tbody>{comparison.subjects.map((s) => (
                <tr key={s.subject_id}>
                  <td>{s.subject_name}</td><td>{s.school_average_score}</td><td>{s.national_average_score ?? '—'}</td>
                  <td>{s.regional_average_score ?? '—'}</td>
                  <td className={s.vs_national > 0 ? 'eip-positive' : s.vs_national < 0 ? 'eip-negative' : ''}>{s.vs_national ?? '—'}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )
        )}
      </div>
    </div>
  );
}

const COMPLIANCE_CATEGORIES = ['accreditation', 'regulatory', 'safety', 'data_protection', 'financial', 'other'];
const COMPLIANCE_STATUSES = ['upcoming', 'compliant', 'overdue', 'expired'];

function ComplianceTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'other', issuing_body: '', requirement_description: '', due_date: '', renewal_frequency_months: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('compliant');
  const [reviewNotes, setReviewNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([executiveReportsService.listComplianceItems(), executiveReportsService.getComplianceSummary()]);
      setItems(i || []);
      setSummary(s);
    } catch (error) {
      toast.error('Failed to load compliance register: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await executiveReportsService.createComplianceItem({
        ...form,
        renewal_frequency_months: form.renewal_frequency_months ? parseInt(form.renewal_frequency_months, 10) : null,
      });
      toast.success('Compliance item added');
      setShowCreate(false);
      setForm({ name: '', category: 'other', issuing_body: '', requirement_description: '', due_date: '', renewal_frequency_months: '' });
      load();
    } catch (error) {
      toast.error('Failed to add item: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openReview = (item) => {
    setReviewTarget(item);
    setReviewStatus(item.status);
    setReviewNotes('');
  };

  const submitReview = async () => {
    try {
      await executiveReportsService.reviewComplianceItem(reviewTarget.id, { status: reviewStatus, notes: reviewNotes || null });
      toast.success('Reviewed');
      setReviewTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to review: ' + error.message);
    }
  };

  const remove = async (id) => {
    try { await executiveReportsService.deleteComplianceItem(id); toast.success('Deleted'); load(); }
    catch (error) { toast.error('Failed to delete: ' + error.message); }
  };

  return (
    <div className="eip-panel">
      {summary && (
        <div className="eip-stat-row">
          <div className="eip-stat"><span className="eip-stat-value">{summary.total}</span><span className="eip-stat-label">Total Items</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{summary.due_within_30_days.length}</span><span className="eip-stat-label">Due in 30 Days</span></div>
          <div className="eip-stat"><span className="eip-stat-value">{summary.overdue.length}</span><span className="eip-stat-label">Overdue</span></div>
        </div>
      )}
      <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add Compliance Item</button>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No compliance items tracked yet</p></div>
      ) : (
        <div className="eip-table"><table>
          <thead><tr><th>Name</th><th>Category</th><th>Issuing Body</th><th>Due Date</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{items.map((i) => (
            <tr key={i.id}>
              <td>{i.name}</td><td>{i.category.replace(/_/g, ' ')}</td><td>{i.issuing_body || '—'}</td><td>{i.due_date || '—'}</td>
              <td><span className={`badge status-${i.status}`}>{i.status.toUpperCase()}</span></td>
              <td className="eip-row-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => openReview(i)}>Review</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(i.id)}>Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Add Compliance Item</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group"><label>Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {COMPLIANCE_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Issuing Body</label><input value={form.issuing_body} onChange={(e) => setForm({ ...form, issuing_body: e.target.value })} /></div>
              <div className="form-group"><label>Requirement Description</label><textarea rows="2" value={form.requirement_description} onChange={(e) => setForm({ ...form, requirement_description: e.target.value })} /></div>
              <div className="eip-picker-row">
                <div className="form-group"><label>Due Date</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div className="form-group"><label>Renewal Frequency (months)</label><input type="number" min="1" value={form.renewal_frequency_months} onChange={(e) => setForm({ ...form, renewal_frequency_months: e.target.value })} /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reviewTarget && (
        <div className="modal-overlay" onClick={() => setReviewTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Review: {reviewTarget.name}</h2><button className="btn-close" onClick={() => setReviewTarget(null)}>×</button></div>
            <div className="form-group">
              <label>Status</label>
              <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
                {COMPLIANCE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Notes</label><textarea rows="2" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} /></div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setReviewTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitReview}>Save Review</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExecutiveInsightsPage;
